import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {
    PriorityCodeMappingRow,
    PricingSettingsRow,
    UserConfigRow,
    AppUser,
    UserAdvertiserConfigRow,
    TierManagementRow,
    UserAdvertiserUploadInfo,
    ChannelUpdateStatus,
    EditWhitelistRow,
    CustomerConfigRow,
    ChannelConfigRow,
    ChannelGroupConfigRow,
    ChannelGroupConfigApiRow,
    PoliticalWindowRow,
    PriorityCodeTierRow,
    CustomerMetricConfigRow,
    ModuleConfigApiRow,
    PoliticalClassesOfTimeRow,
    RatecardMetricGroupRow,
    RoleConfigOverrideableRow,
    LURIncludeExclude,
    PoliticalSettingsRow,
    DeleteChannelRequest,
    UserConfigUpdate,
    MetricAlertDisplayOrderRow,
    HiddenSegmentRow,
    PoliticalWindowInitHistoryRow,
    PoliticalBoundConfigRow,
    PoliticalSettingsUpdateRow,
    EditedSegmentRow,
    EmailedReportList,
    PoliticalDaypartRow,
    AddedSegmentRow,
    RoleConfigPricingTierRowV2,
    RoleConfigDigitalPricingTierRowV2,
    RoleConfigRowV2,
    RoleConfigMetricRowV2,
    MetricAlertSettingsRow,
    DynamicRateCardDaypartRow,
} from '@models/admin/admin';
import { UrlStore } from '@shared/helpers/constants/url-store';
import {
    AccountExecutive,
    ChannelItem,
    DaypartLookupItem,
    Grid,
    GroupedGrid,
    LookupItem,
    MarketItem,
    OrderTypeItem,
    RevenueCodeItem,
    StationItem,
    StringLookupItem,
    TierManagementUpdateItem,
    UserRole,
    UserRoleMetricOverrideable,
} from '@models/lookup';
import { UserAdvertiserConfigFilters } from '@models/user-advertiser-config/user-advertiser-config-filters';
import { RateOpticsModuleConfig, RateOpticsProductConfig, UiConfig } from '@models/config';
import { generateCachedGet } from '@shared/helpers/functions/helpers';
import { switchMap } from 'rxjs/operators';
import { PoliticalWindowFilter, PriorityCodeAdminFilter } from '@models/filter';
import { AuthService } from '@services/auth/auth.service';

@Injectable({
    providedIn: 'root',
})
export class AdminService {
    cacheData = {};
    config!: UiConfig;

    constructor(
        private http: HttpClient,
        private authSvc: AuthService,
    ) {}

    getMetricAlerts(channels: LookupItem[]): Observable<GroupedGrid<MetricAlertSettingsRow>> {
        return this.http.get<GroupedGrid<MetricAlertSettingsRow>>(
            UrlStore.api.admin.metricAlerts.concat('?channels=', channels.map((channel) => channel.id.toString()).join(',')),
        );
    }

    getMetricAlertsDisplayOrder(): Observable<Grid<MetricAlertDisplayOrderRow>> {
        return this.http.get<Grid<MetricAlertDisplayOrderRow>>(UrlStore.api.admin.metricAlertsDisplayOrder);
    }

    saveMetricAlertsDisplayOrder(editedValues: { alertName: string; displayOrder: number }[]): Observable<unknown> {
        return this.http.post<void>(UrlStore.api.admin.metricAlertsDisplayOrder, { editedValues });
    }

    saveMetricAlerts(editedValuesMap: {
        [channelId: number]: { [daypartId: number]: { [metricAlertId: number]: number } };
    }): Observable<unknown> {
        return this.http.post(UrlStore.api.admin.metricAlerts, { editedValues: editedValuesMap });
    }

    getPricingSettings(
        channels: ChannelItem[],
        dayparts: DaypartLookupItem[],
        spotTypes: LookupItem[],
    ): Observable<GroupedGrid<PricingSettingsRow>> {
        return this.http.get<GroupedGrid<PricingSettingsRow>>(
            UrlStore.api.admin.pricingSettings.concat(
                '?channels=',
                channels.map((channel) => channel.id.toString()).join(','),
                '&dayparts=',
                dayparts.map((daypart) => daypart.id.toString()).join(','),
                '&spotTypes=',
                spotTypes.map((spotType) => spotType.id.toString()).join(','),
            ),
        );
    }

    getTierManagement(channels: ChannelItem[], spotTypes: LookupItem[]): Observable<GroupedGrid<TierManagementRow>> {
        return this.http.get<GroupedGrid<TierManagementRow>>(
            UrlStore.api.admin.tierManagement.concat(
                '?channels=',
                channels.map((channel) => channel.id.toString()).join(','),
                '&spotTypes=',
                spotTypes.map((spotType) => spotType.id.toString()).join(','),
            ),
        );
    }

    patchTierManagement(editedValues: TierManagementUpdateItem[]): Observable<void> {
        return this.http.patch<void>(UrlStore.api.admin.tierManagementV2, { editedValues });
    }

    getHiddenSegments(): Observable<Grid<HiddenSegmentRow>> {
        return this.http.get<Grid<HiddenSegmentRow>>(UrlStore.api.internalAdmin.hiddenSegments);
    }

    getEditedSegments(): Observable<Grid<EditedSegmentRow>> {
        return this.http.get<Grid<EditedSegmentRow>>(UrlStore.api.internalAdmin.editedSegments);
    }

    getAddedSegments(): Observable<Grid<AddedSegmentRow>> {
        return this.http.get<Grid<AddedSegmentRow>>(UrlStore.api.internalAdmin.addedSegments);
    }

    deleteAddedSegments(requestIds: number[]): Observable<never> {
        return this.http.delete<never>(UrlStore.api.internalAdmin.addedSegments, { body: { requestIds } });
    }

    deleteHiddenSegments(hiddenSegmentsRows: HiddenSegmentRow[]): Observable<void> {
        const removedHiddenSegments = hiddenSegmentsRows.map((row) => ({
            channelId: row.channelId,
            daypartId: row.daypartId,
            dynamicSegmentation: row.dynamicSegmentation,
            rateCardTab: row.rateCardTab,
            rateCardType: row.rateCardType,
            spotTypeId: row.spotTypeId,
        }));
        return this.http.post<void>(UrlStore.api.internalAdmin.hiddenSegments, { removedHiddenSegments });
    }

    patchPricingSettings(editedValues: unknown): Observable<string[]> {
        return this.http.patch<string[]>(UrlStore.api.admin.pricingSettings, {
            editedValues,
        });
    }

    getRateOpticsPriorityCodeMappings(item: LookupItem): Observable<Grid<PriorityCodeMappingRow>> {
        const url = `${UrlStore.api.admin.priorityCodeMappings}?channel=${item.id}`;
        return this.http.get<Grid<PriorityCodeMappingRow>>(url);
    }

    saveRateOpticsPoliticalCodeMappings(channelId: number, rows: PriorityCodeMappingRow[]): Observable<void> {
        const url = `${UrlStore.api.admin.priorityCodeMappings}`;
        const data = {
            mappings: rows.map((r) => ({
                channelId,
                priorityCodeId: r.priorityCodeId,
                politicalTierId: r.politicalTier?.id ?? null,
                pricingTierId: r.pricingTier?.id ?? null,
            })),
        };
        return this.http.post<void>(url, data);
    }

    savePoliticalSettings(stations: number[], politicalClasses: PoliticalClassesOfTimeRow[]): Observable<void> {
        const data = {
            stations,
            politicalClasses,
        };
        return this.http.post<void>(UrlStore.api.admin.politicalSettingsUpdate, data);
    }

    getPoliticalSettingsUpdate(stations: number[]): Observable<PoliticalSettingsUpdateRow[]> {
        return this.http.get<PoliticalSettingsUpdateRow[]>(UrlStore.api.admin.politicalSettingsUpdate + '?stations=' + stations.join(','));
    }

    getLurExclusionInclusionSettings(stations: number[]): Observable<LURIncludeExclude[]> {
        return this.http.get<LURIncludeExclude[]>(UrlStore.api.admin.lurExclusionInclusion + '?stations=' + stations.join(','));
    }

    getAdvertisersByIds(advIds: string[]): Observable<StringLookupItem[]> {
        return this.http.post<StringLookupItem[]>(UrlStore.api.admin.lurExclusionInclusionAdvertisers, { ids: advIds });
    }

    getAgenciesByIds(agnIds: string[]): Observable<StringLookupItem[]> {
        return this.http.get<StringLookupItem[]>(UrlStore.api.admin.lurExclusionInclusionAgencies + '?ids=' + agnIds.join(','));
    }

    saveLurInclusionExclusions(
        stations: number[],
        revenueCodes1: RevenueCodeItem[],
        revenueCodes2: RevenueCodeItem[],
        revenueCodes3: RevenueCodeItem[],
        orderTypes: OrderTypeItem[],
        advertisers: StringLookupItem[],
        agencies: StringLookupItem[],
    ): Observable<void> {
        const data = {
            stations,
            revenueCodes1,
            revenueCodes2,
            revenueCodes3,
            orderTypes,
            advertisers,
            agencies,
        };
        return this.http.post<void>(UrlStore.api.admin.lurExclusionInclusion, data);
    }

    getUserConfig(
        userAccess: RateOpticsModuleConfig,
        markets: LookupItem[] = [],
        stations: LookupItem[] = [],
        channels: LookupItem[] = [],
        roles: LookupItem[] = [],
        users: LookupItem[] = [],
    ): Observable<Grid<UserConfigRow>> {
        return this.http.post<Grid<UserConfigRow>>(UrlStore.api.userAdmin.config, {
            markets: markets.map((market) => market.id),
            stations: stations.map((station) => station.id),
            channels: channels.map((channel) => channel.id),
            roles: roles.map((role) => role.id),
            users: users.map((user) => user.id),
        });
    }

    getUsers(
        markets: LookupItem[] = [],
        stations: LookupItem[] = [],
        channels: LookupItem[] = [],
        roles: LookupItem[] = [],
        advertiserRestricted: boolean = false,
    ): Observable<LookupItem[]> {
        return this.http.post<LookupItem[]>(UrlStore.api.userAdmin.appUser, {
            markets: markets.map((market) => market.id),
            stations: stations.map((station) => station.id),
            channels: channels.map((channel) => channel.id),
            roles: roles.map((role) => role.id),
            advertiserRestricted,
        });
    }

    patchUser(userId: number, patch: UserConfigUpdate): Observable<void> {
        const url = `${UrlStore.api.userAdmin.appUser}` + `/${userId}`;
        return this.http.patch<void>(url, patch);
    }

    deleteUser(userId: number): Observable<void> {
        const url = `${UrlStore.api.userAdmin.appUser}` + `/${userId}`;
        return this.http.delete<void>(url);
    }

    getDefaultDynamicRateCardReportOptions(userId: number): Observable<LookupItem[]> {
        const url = `${UrlStore.api.userAdmin.appUser}/${userId}/default-dynamic-rate-card-report-options`;
        return this.http.get<LookupItem[]>(url);
    }

    putUser(newUser: AppUser): Observable<AppUser> {
        return this.http.put<AppUser>(UrlStore.api.userAdmin.appUser, newUser);
    }

    bulkImportUserAdvertiserConfig(file: File): Observable<UserAdvertiserUploadInfo[]> {
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        const headers = new Headers();
        headers.append('enctype', 'multipart/form-data');
        headers.append('Accept', 'application/json');
        return this.http.post<UserAdvertiserUploadInfo[]>(UrlStore.api.userAdmin.users, formData);
    }

    getUserRoles(clearCache: boolean = false): Observable<UserRole[]> {
        return generateCachedGet<UserRole[]>(clearCache, UrlStore.api.userAdmin.userRoles, this.http, undefined, this.cacheData);
    }

    getUserAdvertiserConfig(filters: UserAdvertiserConfigFilters): Observable<UserAdvertiserConfigRow[]> {
        let url = `${UrlStore.api.userAdmin.users}`;
        if (filters.markets.Value.length > 0 || filters.stations.Value.length > 0 || filters.channels.Value.length > 0) {
            url += '?invariant=1';

            if (filters.markets.Value.length > 0) {
                url += '&markets=' + filters.markets.Value.map((market) => market.id).join(',');
            }
            if (filters.stations.Value.length > 0) {
                url += '&stations=' + filters.stations.Value.map((station) => station.id).join(',');
            }
            if (filters.channels.Value.length > 0) {
                url += '&channels=' + filters.channels.Value.map((channel) => channel.id).join(',');
            }
            if (filters.users.Value.length > 0 && filters.users.Value !== filters.users.options) {
                url += '&users=' + filters.users.Value.map((user) => user.id).join(',');
            }
        }
        return this.http.get<UserAdvertiserConfigRow[]>(url);
    }

    getAccountExecutives(clearCache: boolean = false): Observable<AccountExecutive[]> {
        return generateCachedGet<AccountExecutive[]>(clearCache, UrlStore.api.userAdmin.trafficAEs, this.http, undefined, this.cacheData);
    }

    getUserAdvertisers(userId: string): Observable<StringLookupItem[]> {
        const url = `${UrlStore.api.userAdmin.userAdvertisersAccess}?userId=${userId}`;
        return this.http.get<StringLookupItem[]>(url);
    }

    getUserAdvertisersNoAccess(userId: string, filters: UserAdvertiserConfigFilters): Observable<StringLookupItem[]> {
        let url = `${UrlStore.api.userAdmin.userAdvertisersNoAccess}?userId=${userId}`;
        if (filters.markets.Value.length > 0) {
            url += '&markets=' + filters.markets.Value.map((market) => market.id).join(',');
        }
        if (filters.stations.Value.length > 0) {
            url += '&stations=' + filters.stations.Value.map((station) => station.id).join(',');
        }
        if (filters.channels.Value.length > 0) {
            url += '&channels=' + filters.channels.Value.map((channel) => channel.id).join(',');
        }
        return this.http.get<StringLookupItem[]>(url);
    }

    saveUserAdvertisers(userId: string, advertisers: string[]): Observable<void> {
        const url = `${UrlStore.api.userAdmin.userAdvertisersAccess}`;
        const data = {
            userId,
            advertisers,
        };
        return this.http.post<void>(url, data);
    }

    getUserMarkets(userId: string): Observable<MarketItem[]> {
        const url = `${UrlStore.api.userAdmin.userMarkets}?userId=${userId}`;
        return this.http.get<MarketItem[]>(url);
    }

    getUserStations(userId: string): Observable<StationItem[]> {
        const url = `${UrlStore.api.userAdmin.userStations}?userId=${userId}`;
        return this.http.get<StationItem[]>(url);
    }

    getUserChannels(userId: string): Observable<ChannelItem[]> {
        const url = `${UrlStore.api.userAdmin.userChannels}?userId=${userId}`;
        return this.http.get<ChannelItem[]>(url);
    }

    getChannelUpdateStatus(channelIds: number[]): Observable<Grid<ChannelUpdateStatus>> {
        let url = UrlStore.api.internalAdmin.channelUpdateStatus;
        if (channelIds.length > 0) {
            url += `?channels=${channelIds}`;
        }
        return this.http.get<Grid<ChannelUpdateStatus>>(url);
    }

    triggerAppRun(
        triggerChannels: number[],
        dropDate: string,
        displayChannels: number[],
        ignoreDataValidation: boolean,
        dbLoadOnly: boolean,
    ): Observable<Grid<ChannelUpdateStatus>> {
        return this.http.post<Grid<ChannelUpdateStatus>>(UrlStore.api.internalAdmin.triggerApp, {
            triggerChannels,
            dropDate,
            displayChannels,
            ignoreDataValidation,
            dbLoadOnly,
        });
    }

    triggerNewChannelAppRun(triggerChannelName: string, dropDate: string): Observable<string> {
        return this.http.post<string>(UrlStore.api.internalAdmin.triggerAppForNewChannel, { triggerChannelName, dropDate });
    }

    refreshAppStatus(channels: number[]): Observable<Grid<ChannelUpdateStatus>> {
        return this.http.post<Grid<ChannelUpdateStatus>>(UrlStore.api.internalAdmin.refreshAppStatus, { channels });
    }

    triggerAppStepFunctionRun(
        triggerMarkets: string[],
        triggerVendor: string,
        dropDate: string,
        ignoreDataValidation: boolean,
    ): Observable<string> {
        return this.http.post<string>(UrlStore.api.internalAdmin.triggerAPPStepFunction, {
            triggerMarkets,
            triggerVendor,
            dropDate,
            ignoreDataValidation,
        });
    }

    deleteChannel(body: DeleteChannelRequest): Observable<unknown> {
        return this.http.post<void>(UrlStore.api.internalAdmin.deleteChannel, body);
    }

    getRoleConfigV2(userRoleIds: number[]): Observable<Grid<RoleConfigRowV2>> {
        return this.http.get<Grid<RoleConfigRowV2>>(UrlStore.api.internalAdmin.roleConfigV2 + `?userRoleIds=${userRoleIds}`);
    }

    saveRoleConfigV2(data: { [key: string]: { capabilityId: number; isEnabled?: boolean }[] }): Observable<Grid<RoleConfigRowV2>> {
        return this.http.patch<Grid<RoleConfigRowV2>>(UrlStore.api.internalAdmin.roleConfigV2, { data });
    }

    getRoleConfigMetricsV2(moduleName: string = '', userRoleIds: number[]): Observable<Grid<RoleConfigMetricRowV2>> {
        return this.http.get<Grid<RoleConfigMetricRowV2>>(
            UrlStore.api.internalAdmin.roleConfigMetricsV2 + `?moduleName=${moduleName}&userRoleIds=${userRoleIds}`,
        );
    }

    getCustomerConfigMetrics(moduleName: string = ''): Observable<Grid<CustomerMetricConfigRow>> {
        return this.http.get<Grid<CustomerMetricConfigRow>>(UrlStore.api.internalAdmin.customerMetric + `?moduleName=${moduleName}`);
    }

    getCustomerConfigRatecardMetrics(): Observable<GroupedGrid<CustomerMetricConfigRow>> {
        return this.http.get<GroupedGrid<CustomerMetricConfigRow>>(UrlStore.api.internalAdmin.customerRatecardMetric);
    }

    saveRoleConfigMetricsV2(data: unknown, moduleName: string): Observable<unknown> {
        return this.http.patch(UrlStore.api.internalAdmin.roleConfigMetricsV2, {
            data,
            moduleName,
        });
    }

    saveCustomerConfigMetrics(
        config: {
            moduleId: number;
            customerMetricDisplayName: string;
            metricName: string;
            tab: number;
            isEnabled: boolean;
            isDefaultLoad?: boolean;
        }[],
        moduleName: string,
    ): Observable<Grid<CustomerMetricConfigRow>> {
        return this.http.patch<Grid<CustomerMetricConfigRow>>(UrlStore.api.internalAdmin.customerMetric, {
            config,
            moduleName,
        });
    }

    saveCustomerConfigRatecardMetrics(
        config: {
            moduleId: number;
            customerMetricDisplayName: string;
            metricName: string;
            tab: number;
            isEnabled: boolean;
            groupId: number;
        }[],
    ): Observable<GroupedGrid<CustomerMetricConfigRow>> {
        return this.http.patch<GroupedGrid<CustomerMetricConfigRow>>(UrlStore.api.internalAdmin.customerRatecardMetric, {
            config,
        });
    }

    getRoleConfigOverrideableV2(userRoleIds: number[]): Observable<Grid<RoleConfigOverrideableRow>> {
        return this.http.get<Grid<RoleConfigOverrideableRow>>(
            UrlStore.api.internalAdmin.roleConfigOverrideableV2 + `?userRoleIds=${userRoleIds}`,
        );
    }

    saveRoleConfigOverrideable(
        userRoleIds: number[],
        config: { metricName: string; isOverrideable: boolean }[],
    ): Observable<Grid<RoleConfigOverrideableRow>> {
        return this.http.patch<Grid<RoleConfigOverrideableRow>>(UrlStore.api.internalAdmin.roleConfigOverrideable, {
            userRoleIds,
            config,
        });
    }

    getRoleConfigMetricsOverrideable(userRoleIds: number[]): Observable<UserRoleMetricOverrideable[]> {
        return this.http.get<UserRoleMetricOverrideable[]>(
            UrlStore.api.internalAdmin.roleConfigMetricsOverrideable + `?userRoleIds=${userRoleIds}`,
        );
    }

    getRoleConfigPricingTiersV2(userRoleIds: number[]): Observable<GroupedGrid<RoleConfigPricingTierRowV2>> {
        return this.http.get<GroupedGrid<RoleConfigPricingTierRowV2>>(
            UrlStore.api.internalAdmin.roleConfigPricingTiersV2 + `?userRoleIds=${userRoleIds}`,
        );
    }

    saveRoleConfigPricingTiersV2(data: object): Observable<unknown> {
        return this.http.patch(UrlStore.api.internalAdmin.roleConfigPricingTiersV2, {
            data,
        });
    }

    getRoleConfigDigitalPricingTiersV2(userRoleIds: number[]): Observable<GroupedGrid<RoleConfigDigitalPricingTierRowV2>> {
        return this.http.get<GroupedGrid<RoleConfigDigitalPricingTierRowV2>>(
            UrlStore.api.internalAdmin.roleConfigDigitalPricingTiersV2 + `?userRoleIds=${userRoleIds}`,
        );
    }

    saveRoleConfigDigitalPricingTiersV2(data: object): Observable<unknown> {
        return this.http.patch(UrlStore.api.internalAdmin.roleConfigDigitalPricingTiersV2, {
            data,
        });
    }

    createUpdateUserRole(userRole: UserRole): Observable<UserRole[]> {
        return this.http.post<UserRole[]>(UrlStore.api.internalAdmin.userRole, { ...userRole });
    }

    deleteUserRole(userRoleId: number): Observable<UserRole[]> {
        return this.http.delete<UserRole[]>(UrlStore.api.internalAdmin.userRole + `?userRoleId=${userRoleId}`);
    }

    cloneUserRole(fromRoleId: number, newRoleName: string): Observable<UserRole[]> {
        return this.http.post<UserRole[]>(UrlStore.api.internalAdmin.cloneUserRole, { fromRoleId, newRoleName });
    }

    getWhitelist(userRoleIds: number[]): Observable<Grid<EditWhitelistRow>> {
        return this.http.get<Grid<EditWhitelistRow>>(UrlStore.api.internalAdmin.whitelist + `?userRoleIds=${userRoleIds}`);
    }

    saveWhitelist(rows: EditWhitelistRow[]): Observable<Grid<EditWhitelistRow>> {
        return this.http.patch<Grid<EditWhitelistRow>>(UrlStore.api.internalAdmin.whitelist, { rows });
    }

    getCustomerConfig(): Observable<Grid<CustomerConfigRow>> {
        return this.http.get<Grid<CustomerConfigRow>>(UrlStore.api.internalAdmin.customerConfig);
    }

    /** every daypart, unscoped by channel access: the sports split is customer-wide */
    getModuleConfigDayparts(): Observable<DaypartLookupItem[]> {
        return this.http.get<DaypartLookupItem[]>(UrlStore.api.internalAdmin.moduleConfigDayparts);
    }

    getModuleConfig(module: string): Observable<Grid<ModuleConfigApiRow>> {
        return this.http.get<Grid<ModuleConfigApiRow>>(UrlStore.api.internalAdmin.moduleConfig + `?module=${module}`);
    }

    getChannelConfig(channels: number[]): Observable<Grid<ChannelConfigRow>> {
        return this.http.get<Grid<ChannelConfigRow>>(UrlStore.api.internalAdmin.channelConfig + `?channels=${channels}`);
    }

    getChannelGroupConfig(channelGroups: number[]): Observable<Grid<ChannelGroupConfigRow>> {
        return this.http
            .get<Grid<ChannelGroupConfigApiRow>>(UrlStore.api.internalAdmin.channelGroupConfig + `?channelGroups=${channelGroups}`)
            .pipe(switchMap(this.mapChannelGroupGrid));
    }

    getEmailedReport(): Observable<Grid<EmailedReportList>> {
        return this.http.get<Grid<EmailedReportList>>(UrlStore.api.userAdmin.emailedReport);
    }

    saveEmailedReportStatus(data: EmailedReportList[]): Observable<Grid<EmailedReportList>> {
        return this.http.patch<Grid<EmailedReportList>>(UrlStore.api.userAdmin.emailedReport, { data });
    }

    patchCustomerConfig(config: CustomerConfigRow[]): Observable<Grid<CustomerConfigRow>> {
        return this.http.patch<Grid<CustomerConfigRow>>(UrlStore.api.internalAdmin.customerConfig, { config });
    }

    patchModuleConfig(config: ModuleConfigApiRow[]): Observable<Grid<ModuleConfigApiRow>> {
        return this.http.patch<Grid<ModuleConfigApiRow>>(UrlStore.api.internalAdmin.moduleConfig, { config });
    }

    patchChannelConfig(config: ChannelConfigRow[]): Observable<Grid<ChannelConfigRow>> {
        return this.http.patch<Grid<ChannelConfigRow>>(UrlStore.api.internalAdmin.channelConfig, { config });
    }

    patchChannelGroupConfig(config: ChannelGroupConfigApiRow[]): Observable<Grid<ChannelGroupConfigRow>> {
        return this.http
            .patch<Grid<ChannelGroupConfigApiRow>>(UrlStore.api.internalAdmin.channelGroupConfig, { config })
            .pipe(switchMap(this.mapChannelGroupGrid));
    }

    getPriorityCodeTiers(filters: PriorityCodeAdminFilter): Observable<Grid<PriorityCodeTierRow>> {
        return this.http.get<Grid<PriorityCodeTierRow>>(UrlStore.api.admin.priorityCodeTiers + `?channels=${filters.channels}`);
    }

    getPriorityCodeTiersV2(filters: PriorityCodeAdminFilter): Observable<Grid<PriorityCodeTierRow>> {
        return this.http.get<Grid<PriorityCodeTierRow>>(
            UrlStore.api.admin.priorityCodeTiersV2 + `?channels=${filters.channels}&quarters=${filters.quarters}`,
        );
    }

    updatePriorityCodeTiers(edits: PriorityCodeTierRow[]): Observable<void> {
        return this.http.patch<void>(UrlStore.api.admin.priorityCodeTiers, { edits });
    }

    renamePriorityCodeTiers(renames: { channelId: number; tierId: number; tierName: string }[]): Observable<void> {
        return this.http.post<void>(UrlStore.api.admin.priorityCodeTiersRename, { renames });
    }

    savePriorityCodeTiers(data: PriorityCodeTierRow[]): Observable<void> {
        const url = UrlStore.api.admin.priorityCodeTiers;
        return this.http.post<void>(url, {
            tierData: data,
        });
    }

    getPoliticalWindows(filters: PoliticalWindowFilter): Observable<Grid<PoliticalWindowRow>> {
        return this.http.get<Grid<PoliticalWindowRow>>(
            UrlStore.api.admin.politicalWindows +
                `?markets=${filters.markets}` +
                `&startDate=${encodeURIComponent(filters.startDate)}` +
                `&endDate=${encodeURIComponent(filters.endDate)}`,
        );
    }

    getPoliticalInitTiersHistory(channels: number[], politicalWindows: number[]): Observable<Grid<PoliticalWindowInitHistoryRow>> {
        return this.http.get<Grid<PoliticalWindowInitHistoryRow>>(
            UrlStore.api.lookupsV2.politicalInitTiersHistory + `?channelIds=${channels}&politicalWindows=${politicalWindows}`,
        );
    }

    getPoliticalSettings(): Observable<Grid<PoliticalSettingsRow>> {
        return this.http.get<Grid<PoliticalSettingsRow>>(UrlStore.api.lookupsV2.politicalSettings);
    }

    savePoliticalWindow(
        markets: number[],
        startDate: string,
        endDate: string,
        windowName: string,
        filters: PoliticalWindowFilter,
    ): Observable<Grid<PoliticalWindowRow>> {
        return this.http.put<Grid<PoliticalWindowRow>>(UrlStore.api.admin.politicalWindows, {
            markets,
            startDate,
            endDate,
            windowName,
        });
    }

    resetAndReloadPoliticalWindows(
        filters: { markets: number[]; startDate?: string; endDate?: string; windowName?: string; channelGroup?: number[] },
        subscription: PushSubscriptionJSON,
        email: string,
    ): Observable<{ success: boolean }> {
        const endpoint = subscription && subscription.endpoint ? subscription.endpoint : null;
        const p256dh = subscription && subscription.keys ? subscription.keys.p256dh : null;
        const auth = subscription && subscription.keys ? subscription.keys.auth : null;
        return this.http.get<{ success: boolean }>(
            UrlStore.api.admin.politicalWindowsReset +
                `?markets=${filters.markets}` +
                `${filters.startDate ? '&startDate=' + encodeURIComponent(filters.startDate) : ''}` +
                `${filters.endDate ? '&endDate=' + encodeURIComponent(filters.endDate) : ''}` +
                `${endpoint ? '&endpoint=' + encodeURIComponent(endpoint) : ''}` +
                '&expirationTime=null' +
                `${p256dh ? '&p256dh=' + encodeURIComponent(p256dh) : ''}` +
                `${auth ? '&auth=' + encodeURIComponent(auth) : ''}` +
                `&email=${email}`,
        );
    }

    editPoliticalWindowV2(
        windowIds: number[],
        markets: number[],
        newMarkets: number[],
        startDate: string,
        endDate: string,
        windowName: string,
        filters: PoliticalWindowFilter,
    ): Observable<Grid<PoliticalWindowRow>> {
        return this.http.patch<Grid<PoliticalWindowRow>>(
            UrlStore.api.admin.politicalWindowsV2 +
                `?markets=${filters.markets}` +
                (filters.startDate ? `&startDate=${encodeURIComponent(filters.startDate)}` : '') +
                (filters.endDate ? `&endDate=${encodeURIComponent(filters.endDate)}` : ''),
            {
                markets,
                newMarkets,
                windowIds,
                startDate,
                endDate,
                windowName,
            },
        );
    }

    editPoliticalWindow(
        windowId: number,
        startDate: string,
        endDate: string,
        windowName: string,
        filters: PoliticalWindowFilter,
    ): Observable<Grid<PoliticalWindowRow>> {
        return this.http.patch<Grid<PoliticalWindowRow>>(
            UrlStore.api.admin.politicalWindows +
                `?markets=${filters.markets}` +
                (filters.startDate ? `&startDate=${encodeURIComponent(filters.startDate)}` : '') +
                (filters.endDate ? `&endDate=${encodeURIComponent(filters.endDate)}` : ''),
            {
                windowId,
                startDate,
                endDate,
                windowName,
            },
        );
    }

    deletePoliticalWindow(windowId: number, filters: PoliticalWindowFilter): Observable<Grid<PoliticalWindowRow>> {
        return this.http.delete<Grid<PoliticalWindowRow>>(
            UrlStore.api.admin.politicalWindows +
                `?markets=${filters.markets}` +
                (filters.startDate ? `&startDate=${encodeURIComponent(filters.startDate)}` : '') +
                (filters.endDate ? `&endDate=${encodeURIComponent(filters.endDate)}` : ''),
            {
                body: {
                    windowId,
                },
            },
        );
    }

    getModules(screenName: string = 'customerMetricConfig'): Observable<LookupItem[]> {
        if (screenName === 'customerMetricConfig') {
            return of([
                { name: 'rate_generator', displayName: 'Rate Generator', id: 1 },
                { name: 'inventory_insights', displayName: 'Inventory Insights', id: 2 },
                { name: 'advertiser_insights', displayName: 'Advertiser Insights', id: 3 },
                { name: 'rate_card_special_events', displayName: 'Rate Card Special Events', id: 4 },
                { name: 'high_impact_program_report', displayName: 'High Impact Program Report', id: 5 },
            ]);
        } else if (screenName === 'roleMetricConfig') {
            return of([
                { name: 'rate_generator', displayName: 'Rate Generator', id: 1 },
                { name: 'political_rate_card', displayName: 'Political Rate Card', id: 2 },
                { name: 'inventory_insights', displayName: 'Inventory Insights', id: 3 },
                { name: 'dynamic_rate_card', displayName: 'Dynamic Rate Card', id: 4 },
                { name: 'digital_rate_card', displayName: 'Digital Rate Card', id: 5 },
                { name: 'high_impact_program_report', displayName: 'High Impact Program Report', id: 6 },
            ]);
        } else if (screenName === 'customerRatecardMetricConfig') {
            this.config = this.authSvc.getConfig();
            const accessConfig = this.config.customerCapability.rate_optics;
            type ProductModuleKey = keyof RateOpticsProductConfig;
            let moduleNamesFilter: { name: ProductModuleKey; displayName: string; id: number }[] = [
                { name: 'dynamic_rate_card', displayName: 'Dynamic Rate Card', id: 1 },
                { name: 'political_rate_card', displayName: 'Political Rate Card', id: 2 },
                { name: 'digital_rate_card', displayName: 'Digital Rate Card', id: 3 },
            ];
            moduleNamesFilter = moduleNamesFilter.filter(
                (option) =>
                    accessConfig &&
                    accessConfig[option.name] &&
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (accessConfig[option.name as keyof RateOpticsProductConfig] as any)?.access,
            );
            return of(moduleNamesFilter);
        }
        return of([]);
    }

    getRatecardMetricGroup(): Observable<Grid<RatecardMetricGroupRow>> {
        return this.http.get<Grid<RatecardMetricGroupRow>>(UrlStore.api.internalAdmin.ratecardMetricGroup + '?forDropdown=false');
    }

    getRatecardMetricGroupForDropdown(clearCache: boolean = false): Observable<LookupItem[]> {
        return generateCachedGet<LookupItem[]>(
            clearCache,
            UrlStore.api.internalAdmin.ratecardMetricGroup + '?forDropdown=true',
            this.http,
            undefined,
            this.cacheData,
        );
    }

    patchRatecardMetricGroup(groups: { id: number; groupName: string; displayOrder: string }[]): Observable<Grid<RatecardMetricGroupRow>> {
        return this.http.patch<Grid<RatecardMetricGroupRow>>(UrlStore.api.internalAdmin.ratecardMetricGroup, { groups });
    }

    deleteRatecardMetricGroup(ratecardMetricGroupId: number): Observable<Grid<RatecardMetricGroupRow>> {
        const url = `${UrlStore.api.internalAdmin.ratecardMetricGroup}` + `/${ratecardMetricGroupId}`;
        return this.http.delete<Grid<RatecardMetricGroupRow>>(url);
    }

    getPoliticalBoundTiers(): Observable<Grid<PoliticalBoundConfigRow>> {
        return this.http.get<Grid<PoliticalBoundConfigRow>>(UrlStore.api.internalAdmin.politicalBoundConfig);
    }

    updatePoliticalBoundTiers(
        politicalBoundTiers: { channelId: number; politicalTierId: number }[],
    ): Observable<Grid<PoliticalBoundConfigRow>> {
        return this.http.patch<Grid<PoliticalBoundConfigRow>>(UrlStore.api.internalAdmin.politicalBoundConfig, { politicalBoundTiers });
    }

    getPoliticalDayparts(): Observable<Grid<PoliticalDaypartRow>> {
        return this.http.get<Grid<PoliticalDaypartRow>>(UrlStore.api.internalAdmin.politicalDayparts);
    }

    deletePoliticalDaypart(channelIds: number[], daypartId: number): Observable<never> {
        return this.http.delete<never>(UrlStore.api.internalAdmin.politicalDayparts, { body: { channelIds, daypartId } });
    }

    createPoliticalDaypart(channelIds: number[], daypartId: number): Observable<never> {
        return this.http.post<never>(UrlStore.api.internalAdmin.politicalDayparts, { channelIds, daypartId });
    }

    getDynamicRateCardDayparts(): Observable<Grid<DynamicRateCardDaypartRow>> {
        return this.http.get<Grid<DynamicRateCardDaypartRow>>(UrlStore.api.internalAdmin.dynamicRateCardDayparts);
    }

    deleteDynamicRateCardDaypart(channelIds: number[], daypartId: number): Observable<never> {
        return this.http.delete<never>(UrlStore.api.internalAdmin.dynamicRateCardDayparts, { body: { channelIds, daypartId } });
    }

    createDynamicRateCardDaypart(channelIds: number[], daypartId: number): Observable<never> {
        return this.http.post<never>(UrlStore.api.internalAdmin.dynamicRateCardDayparts, { channelIds, daypartId });
    }

    createProposal(proposalName: string): Observable<never> {
        return this.http.post<never>(UrlStore.api.aida.createProposal, { proposalName });
    }

    getUnmatchedLogins(): Observable<object[]> {
        return this.http.get<object[]>(UrlStore.api.internalAdmin.unmatchedLogins);
    }

    resolveUnmatchedLogin(unmatchedLoginAttemptId: number, linkedUserId: number): Observable<object[]> {
        return this.http.post<object[]>(UrlStore.api.internalAdmin.resolveUnmatchedLogin, {
            unmatchedLoginAttemptId,
            linkedUserId,
        });
    }

    dismissUnmatchedLogin(unmatchedLoginAttemptId: number): Observable<object[]> {
        return this.http.post<object[]>(UrlStore.api.internalAdmin.dismissUnmatchedLogin, {
            unmatchedLoginAttemptId,
        });
    }

    getEmailAliases(): Observable<object[]> {
        return this.http.get<object[]>(UrlStore.api.internalAdmin.emailAliases);
    }

    deleteEmailAlias(emailAliasId: number): Observable<object[]> {
        return this.http.post<object[]>(UrlStore.api.internalAdmin.deleteEmailAlias, {
            emailAliasId,
        });
    }

    private mapChannelGroupGrid(grid: Grid<ChannelGroupConfigApiRow>): Observable<Grid<ChannelGroupConfigRow>> {
        const mappedGrid: Grid<ChannelGroupConfigRow> = {
            columns: grid.columns,
            data: grid.data.map((row) => ({
                ...row,
                defaultSpotLengthIds: (row.defaultSpotLengthIds || [])?.map((spotLength) => ({ id: spotLength, name: '' })),
                defaultPriorityCodes: (row.defaultPriorityCodes || [])?.map((priorityCode) => ({
                    id: priorityCode,
                    name: '',
                })),
                defaultSpotTypesInventoryInsights: (row.defaultSpotTypesInventoryInsights || [])?.map((spotType) => ({
                    id: spotType,
                    name: '',
                })),
                defaultSpotTypesRateCard: (row.defaultSpotTypesRateCard || [])?.map((spotType) => ({
                    id: spotType,
                    name: '',
                })),
                defaultSpotTypesRateGen: (row.defaultSpotTypesRateGen || [])?.map((spotType) => ({
                    id: spotType,
                    name: '',
                })),
                defaultPeriodRateCardDynamic: [row.defaultPeriodRateCardDynamic]?.map((periodType) => ({
                    id: periodType,
                    name: '',
                }))[0],
                defaultPeriodRateCardPolitical: [row.defaultPeriodRateCardPolitical]?.map((periodType) => ({
                    id: periodType,
                    name: '',
                }))[0],
                defaultPeriodRateCardDigital: [row.defaultPeriodRateCardDigital]?.map((periodType) => ({
                    id: periodType,
                    name: '',
                }))[0],
                defaultPeriodInventoryInsights: [row.defaultPeriodInventoryInsights]?.map((periodType) => ({
                    id: periodType,
                    name: '',
                }))[0],
                defaultTiersRateCardDynamic: (row.defaultTiersRateCardDynamic || [])?.map((tier) => ({
                    id: tier,
                    name: '',
                })),
                defaultTiersRateCardPolitical: (row.defaultTiersRateCardPolitical || [])?.map((tier) => ({
                    id: tier,
                    name: '',
                })),
                defaultTiersRateCardDigital: (row.defaultTiersRateCardDigital || [])?.map((tier) => ({
                    id: tier,
                    name: '',
                })),
                defaultTiersRateGen: (row.defaultTiersRateGen || [])?.map((tier) => ({
                    id: tier,
                    name: '',
                })),
            })),
        };
        return of(mappedGrid);
    }
}
